/**
 * Bundle Cart — single owner of the bundle + free-product cart-write path.
 *
 * Replaces three files that used to fight over the cart:
 *   - offer-bundle-cart.js      (augmented /cart/add via a fetch hook)
 *   - offer-bundle-cleanup.js   (cascaded removals via a fetch hook)
 *   - the ATC / batch-add logic inside bundle-selector.js
 *
 * Property contract (final):
 *   _product-type  "main-product" | "free-product"   (HIDDEN — leading underscore)
 *   _bundle_id     UUID grouping every line added in one ATC click (hidden)
 *   Gift Included  visible label, ONLY on the first main-product line: "🎁 <labels>"
 *
 * Why _product-type is hidden: as a VISIBLE property it changed the line-item
 * key when drawers/apps/Horizon re-render dropped or reordered it, and key churn
 * made the cascade diff misread one bundle's lines as another's. Hidden keys are
 * stable, and we additionally diff by a composite fingerprint (see below), never
 * by key alone.
 *
 * How it works in THIS theme (Horizon):
 *   - Horizon routes every declarative handler through ONE document-level capture
 *     listener (component.js) that reads the closest `on:submit` attribute at
 *     dispatch time. We neutralise the product form's `on:submit` (set it to "")
 *     for forms we own, so Horizon's product-form-component / bundle handler bails,
 *     and we do the add ourselves from a single capture-phase submit listener.
 *     This is order-independent: even if Horizon's listener runs, it finds no
 *     method and does nothing; our listener always preventDefault()s.
 *   - There is no `cart:refresh:completed` event. The one cart event is
 *     `cart:update` (ThemeEvents.cartUpdate). The drawer (cart-drawer-component,
 *     auto-open) opens on it and cart-items-component re-renders from
 *     detail.data.sections. So after our add we dispatch a CartAdd-shaped
 *     `cart:update` carrying the fresh cart + the section HTML the add returned.
 *   - Cascade removal is triggered by a READ-ONLY fetch observer that fires ONLY
 *     for a true user removal: POST /cart/change.js with {quantity: 0}. Every
 *     other cart mutation (add, quantity change, /cart/update.js, drawer refresh)
 *     just refreshes the snapshot — this is what stops Horizon's internal cart
 *     refreshes from provoking phantom cascades. Our own cart writes carry an
 *     X-Bundle-Cart-Internal header so the observer ignores them.
 *   - Cascade tears a bundle down ONLY when ALL of its main-product lines are
 *     gone. A "Buy 2" bundle has two main lines; removing one leaves the bundle
 *     intact, removing the last one tears down the remaining freebies.
 *   - The diff identifies lines by a composite FINGERPRINT
 *     (bundle_id :: variant_id :: product_type), never by Shopify's line `key`,
 *     which can regenerate on mutation.
 *
 * Shopify note: POST /cart/add.js with an items array is ATOMIC — if ANY line
 * fails, the whole request 4xx's and nothing is added. So "add main, skip only
 * the failed freebies" is implemented as: try main+freebies; on failure retry
 * main-only. If main-only also fails, nothing was added (roll back cleanly).
 */
(function () {
  'use strict';

  if (window.__bundleCartInstalled) return;
  window.__bundleCartInstalled = true;

  const CART_ADD_URL = (window.Theme?.routes?.cart_add_url) || '/cart/add.js';
  const CART_CHANGE_URL = (window.Theme?.routes?.cart_change_url) || '/cart/change.js';
  const CART_URL = '/cart.js';

  const PROP_TYPE = '_product-type';          // hidden (leading underscore) — see migration note
  const LEGACY_PROP_TYPE = 'product-type';    // pre-migration visible key; still READ so legacy bundles cascade
  const PROP_BUNDLE_ID = '_bundle_id';
  const DEFAULT_GIFT_KEY = 'Gift Included';

  const TYPE_MAIN = 'main-product';
  const TYPE_FREE = 'free-product';

  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  // Marks our OWN cart writes so the mutation observer ignores them (no self-triggered cascade).
  const INTERNAL_HEADERS = { ...JSON_HEADERS, 'X-Bundle-Cart-Internal': '1' };

  const newBundleId = () =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `b_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  // ---------- Cart snapshot (for cascade diffing) ----------

  /** @type {any} */
  let prevSnapshot = null;
  let reconcileLock = false;
  let pendingReconcile = false;
  let atcBusy = false;

  // ---------- Property helpers (understand BOTH the new and legacy schemes) ----------

  const propsOf = (it) => (it && it.properties) || {};
  const bundleIdOf = (it) => propsOf(it)[PROP_BUNDLE_ID] || null;
  const variantIdOf = (it) => it?.variant_id || it?.id || '';

  /**
   * Canonical product-type for a line, tolerant of the migration:
   *   - new hidden key `_product-type`
   *   - legacy visible key `product-type`
   *   - legacy `_bundle_role` (main/bundle → main-product, offer → free-product)
   */
  function typeOf(it) {
    const p = propsOf(it);
    if (p[PROP_TYPE]) return p[PROP_TYPE];
    if (p[LEGACY_PROP_TYPE]) return p[LEGACY_PROP_TYPE];
    const role = p._bundle_role;
    if (role === 'main' || role === 'bundle') return TYPE_MAIN;
    if (role === 'offer') return TYPE_FREE;
    return '';
  }

  const isMain = (it) => typeOf(it) === TYPE_MAIN;
  const isFree = (it) => typeOf(it) === TYPE_FREE;

  /**
   * Composite identity: (bundle_id :: variant_id :: product_type). Two items with
   * the same fingerprint are the same logical line even if Shopify regenerates
   * their `key` during cart mutations — this is what makes the cascade diff
   * resilient to key churn (the root cause of the cross-bundle removal bug).
   */
  function fingerprint(it) {
    return `${bundleIdOf(it) || ''}::${variantIdOf(it)}::${typeOf(it)}`;
  }

  // ---------- Utilities ----------

  async function fetchCart() {
    try {
      const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
      return res.ok ? await res.json() : { items: [] };
    } catch (_) {
      return { items: [] };
    }
  }

  function getUnlockedOffers() {
    const nodes = document.querySelectorAll('.offer-item.is-unlocked[data-free-variant-id]');
    const seen = new Set();
    const out = [];
    for (const el of nodes) {
      const variantId = Number(el.dataset.freeVariantId);
      if (!variantId || seen.has(variantId)) continue;
      seen.add(variantId);
      out.push({ variantId, label: el.dataset.freeProductLabel || 'Free Gift' });
    }
    return out;
  }

  function getGiftConfig() {
    const cfg = (typeof window !== 'undefined' && window.__giftPropertyConfig) || {};
    return {
      key: (typeof cfg.key === 'string' && cfg.key.trim()) || DEFAULT_GIFT_KEY,
      emoji: typeof cfg.emoji === 'string' ? cfg.emoji : '🎁',
    };
  }

  function buildGiftValue(unlocked) {
    if (!unlocked || !unlocked.length) return null;
    const { emoji } = getGiftConfig();
    const label = 'Free Gifts';
    return emoji ? `${emoji} ${label}` : label;
  }

  /** Section ids that render cart-items (drawer + cart page) — for section rendering. */
  function getSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids).join(',');
  }

  // ---------- Main-variant resolution ----------

  /**
   * Determine the main variant id(s) and per-line quantity for a form.
   * Prefers the bundle component's public API; falls back to DOM scraping;
   * finally falls back to the plain product variant + quantity input.
   * @param {HTMLFormElement} form
   * @returns {{ ids: number[], perItemQuantity: number, bundle: boolean }}
   */
  function getMainSelections(form) {
    const section = form.closest('.shopify-section') || document;

    const bundleComp = section.querySelector('bundle-group-component');
    if (bundleComp && typeof bundleComp.getActiveSelections === 'function') {
      const ids = bundleComp.getActiveSelections();
      if (Array.isArray(ids) && ids.length) return { ids, perItemQuantity: 1, bundle: true };
    }

    const scraped = scrapeActiveTier(section, form);
    if (scraped.length) return { ids: scraped, perItemQuantity: 1, bundle: true };

    const idInput = /** @type {HTMLInputElement|null} */ (form.querySelector('[name="id"]'));
    const id = idInput ? Number(idInput.value) : 0;
    const qtyInput = /** @type {HTMLInputElement|null} */ (form.querySelector('[name="quantity"]'));
    const qty = qtyInput ? Math.max(1, Number(qtyInput.value) || 1) : 1;
    return { ids: id ? [id] : [], perItemQuantity: qty, bundle: false };
  }

  /**
   * Fallback DOM read of the active bundle tier (used only if the component API
   * is unavailable). Mirrors bundle-group-component's tier→variant logic.
   */
  function scrapeActiveTier(section, form) {
    const tiers = section.querySelectorAll('[data-bundle-item]');
    /** @type {Element|null} */
    let active = null;
    for (const tier of tiers) {
      const radio = /** @type {HTMLInputElement|null} */ (tier.querySelector('[data-bundle-radio]'));
      if (radio?.checked || tier.classList.contains('is-selected')) {
        active = tier;
        break;
      }
    }
    if (!active) return [];

    const quantity = Number(active.getAttribute('data-bundle-quantity')) || 1;
    const ids = [];
    active.querySelectorAll('[data-bundle-variant-select]').forEach((sel) => {
      const v = Number(/** @type {HTMLInputElement|HTMLSelectElement} */ (sel).value);
      if (v > 0) ids.push(v);
    });
    if (ids.length === quantity) return ids;

    const idInput = /** @type {HTMLInputElement|null} */ (form.querySelector('[name="id"]'));
    const fallback = idInput ? Number(idInput.value) : 0;
    if (!fallback) return ids;
    while (ids.length < quantity) ids.push(fallback);
    return ids;
  }

  /** Custom `properties[...]` line-item props on the form (e.g. gift-card recipient). */
  function readExtraProperties(form) {
    const out = {};
    for (const [key, value] of new FormData(form).entries()) {
      if (key.startsWith('properties[') && key.endsWith(']')) {
        out[key.slice('properties['.length, -1)] = value;
      }
    }
    return out;
  }

  // ---------- Loader (styles in assets/bundle-cart.css) ----------

  function setButtonLoading(btn, isLoading) {
    if (!btn) return;

    if (isLoading) {
      // Store original content so we can restore it on release.
      if (!btn.dataset.originalContent) {
        btn.dataset.originalContent = btn.innerHTML;
      }

      // Lock button dimensions BEFORE mutating content — prevents layout shift
      // and stops the button from collapsing to an empty box.
      const rect = btn.getBoundingClientRect();
      btn.style.minWidth = `${rect.width}px`;
      btn.style.minHeight = `${rect.height}px`;

      btn.innerHTML = '<span class="bundle-atc-spinner" aria-hidden="true"></span>';
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
    } else {
      if (btn.dataset.originalContent) {
        btn.innerHTML = btn.dataset.originalContent;
        delete btn.dataset.originalContent;
      }
      btn.style.minWidth = '';
      btn.style.minHeight = '';
      btn.classList.remove('is-loading');
      btn.setAttribute('aria-busy', 'false');
      btn.disabled = false;
    }
  }

  function showInlineError(form, message) {
    const errEl = form.querySelector('[ref="addToCartTextError"]');
    if (!errEl) {
      console.error('[bundle-cart]', message);
      return;
    }
    errEl.classList.remove('hidden');
    // Markup is: <span class="svg-wrapper">…</span>{text}. Reuse/append the text node.
    const textNode = errEl.childNodes[2];
    if (textNode) textNode.textContent = message;
    else errEl.appendChild(document.createTextNode(message));
    setTimeout(() => errEl.classList.add('hidden'), 10000);
  }

  // ---------- Cart write ----------

  /** POST one items array. Returns { ok, sections?, error? }. */
  async function postAdd(items) {
    const payload = { items, sections: getSectionIds(), sections_url: window.location.pathname };
    let res;
    try {
      res = await fetch(CART_ADD_URL, { method: 'POST', headers: INTERNAL_HEADERS, body: JSON.stringify(payload) });
    } catch (err) {
      return { ok: false, error: { message: 'Network error', description: String(err) } };
    }
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      return { ok: false, error };
    }
    const body = await res.json().catch(() => ({}));
    return { ok: true, sections: body.sections };
  }

  /**
   * Build the atomic items array and add it. Rolls freebies off on failure so the
   * customer still gets the main product when only a freebie is the problem.
   * @param {HTMLFormElement} form
   */
  async function addBundleAndFreebies(form) {
    const { ids: mainVariantIds, perItemQuantity } = getMainSelections(form);
    if (!mainVariantIds.length) {
      return { ok: false, message: 'Please choose a variant before adding to cart.' };
    }

    const bundleId = newBundleId();
    const unlocked = getUnlockedOffers();
    const giftValue = buildGiftValue(unlocked);
    const giftCfg = getGiftConfig();
    const extraProps = readExtraProperties(form);

    // Group identical variant ids so Shopify merges same-variant bundles into ONE line
    // at qty:N (a "Buy 2" of Red+Red → qty:2). Different variants (Red + Blue) keep as
    // separate lines because their id differs. For a plain (non-bundle) add there is one
    // id and perItemQuantity carries the chosen quantity; each bundle slot counts as 1.
    const variantQuantities = new Map();
    for (const vid of mainVariantIds) {
      variantQuantities.set(vid, (variantQuantities.get(vid) || 0) + perItemQuantity);
    }

    // One shared property set for EVERY main line — including Gift Included — so
    // same-variant mains are byte-identical and Shopify's line-merging kicks in.
    const mainProperties = {
      ...extraProps,
      [PROP_TYPE]: TYPE_MAIN,
      [PROP_BUNDLE_ID]: bundleId,
      ...(giftValue ? { [giftCfg.key]: giftValue } : {}),
    };

    const mainItems = Array.from(variantQuantities.entries()).map(([vid, qty]) => ({
      id: vid,
      quantity: qty,
      properties: mainProperties,
    }));

    const freeItems = unlocked.map((o) => ({
      id: o.variantId,
      quantity: 1,
      properties: { [PROP_TYPE]: TYPE_FREE, [PROP_BUNDLE_ID]: bundleId },
    }));

    // Attempt 1: everything, atomically.
    let result = await postAdd([...mainItems, ...freeItems]);

    // Attempt 2: only freebies failed → retry with the main product alone.
    if (!result.ok && freeItems.length) {
      console.warn('[bundle-cart] atomic add failed; retrying without freebies', result.error);
      result = await postAdd(mainItems);
    }

    if (!result.ok) {
      const err = result.error || {};
      return { ok: false, message: err.description || err.message || 'Could not add to cart.' };
    }

    const cart = await fetchCart();
    return { ok: true, cart, sections: result.sections };
  }

  // ---------- UI refresh ----------

  /**
   * Drive Horizon's native cart UI: dispatch a CartAdd-shaped `cart:update` so
   * cart-items-component re-renders (from data.sections) and the auto-open cart
   * drawer opens. This replaces the fictional `cart:refresh` events.
   */
  function dispatchCartUpdate(cart, sections, source) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || undefined,
          sourceId: 'bundle-cart',
          data: { source, itemCount: cart?.item_count, sections },
        },
      })
    );
  }

  /**
   * Re-render the cart UI after a REMOVAL (cascade or load-time cleanup) so no
   * stale/ghost rows remain — a ghost row makes Horizon's line-based
   * /cart/change target a line that no longer exists → 422. We must refresh, but
   * a removal should never pop the drawer open, so we strip `auto-open` for the
   * duration of the (synchronous) dispatch and restore it immediately after.
   */
  function refreshCartUISilently(cart, sections, source) {
    const drawers = document.querySelectorAll('cart-drawer-component[auto-open]');
    drawers.forEach((d) => d.removeAttribute('auto-open'));
    dispatchCartUpdate(cart, sections, source); // cart:update listeners run synchronously
    drawers.forEach((d) => d.setAttribute('auto-open', ''));
  }

  /** Resolve once the drawer has finished opening, or after a timeout. */
  function waitForDrawerSettled(timeoutMs = 1500) {
    return new Promise((resolve) => {
      const drawer = document.querySelector('cart-drawer-component');
      const dialog = drawer?.querySelector('dialog');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        observer?.disconnect();
        requestAnimationFrame(() => resolve());
      };
      const timer = setTimeout(finish, timeoutMs);

      // No drawer on this page (e.g. cart page) — settle on the next frame.
      if (!dialog) {
        requestAnimationFrame(finish);
        return;
      }
      if (dialog.open) {
        finish();
        return;
      }
      var observer =
        typeof MutationObserver !== 'undefined'
          ? new MutationObserver(() => {
              if (dialog.open) finish();
            })
          : null;
      observer?.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    });
  }

  // ---------- ATC submit (single capture-phase listener) ----------

  async function onSubmitCapture(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.bundleCartOwned !== 'true') return;

    // We own this add. Stop Horizon's native path and the browser navigation.
    event.preventDefault();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    if (atcBusy) return;
    if (!form.checkValidity()) {
      form.reportValidity?.();
      return;
    }

    const btn = form.querySelector('add-to-cart-component button, button[name="add"], [type="submit"]');
    if (btn?.disabled) return;

    atcBusy = true;
    setButtonLoading(btn, true);

    try {
      const result = await addBundleAndFreebies(form);
      if (!result.ok) {
        showInlineError(form, result.message);
        return; // do NOT open the drawer on a rolled-back add
      }
      dispatchCartUpdate(result.cart, result.sections, 'bundle-cart');
      await waitForDrawerSettled();
      prevSnapshot = result.cart || (await fetchCart());
    } catch (err) {
      console.error('[bundle-cart] ATC failed', err);
      showInlineError(form, 'Sorry, could not add to cart. Please try again.');
    } finally {
      setButtonLoading(btn, false);
      atcBusy = false;
    }
  }

  // ---------- Cascade removal ----------

  /**
   * A user removed a line. Diff by fingerprint (NOT key). Tear a bundle down only
   * when EVERY one of its main-product lines is gone; a bundle that still has a
   * main line (e.g. one of two "Buy 2" mains removed) is left intact.
   */
  async function reconcile() {
    if (reconcileLock) {
      pendingReconcile = true;
      return;
    }
    reconcileLock = true;
    pendingReconcile = false;

    // Track whether we grabbed the loader so we only release() if we hold().
    let didHoldLoader = false;

    try {
      const newCart = await fetchCart();
      const prevItems = prevSnapshot?.items || [];
      const newFingerprints = new Set(newCart.items.map(fingerprint));

      // Main-product lines whose fingerprint no longer appears in the cart.
      const removedMains = prevItems.filter((it) => {
        if (!isMain(it) || !bundleIdOf(it)) return false;
        return !newFingerprints.has(fingerprint(it));
      });
      if (!removedMains.length) {
        prevSnapshot = newCart;
        return;
      }

      // Rule: ANY main removal from a bundle tears down the ENTIRE bundle.
      // A "Buy 2 Get 1 Free" is invalid with only part of it in the cart, so we
      // remove every remaining line (other mains + all freebies) sharing that
      // _bundle_id. Applies whether the removed main was a merged same-variant
      // line (qty:N → 0) or one of several different-variant main lines.
      const bundlesToTeardown = new Set(removedMains.map(bundleIdOf));

      // Remove every remaining line in a torn-down bundle. Match by _bundle_id ONLY.
      const keysToRemove = newCart.items
        .filter((it) => bundlesToTeardown.has(bundleIdOf(it)) && (it.quantity || 0) > 0)
        .map((it) => it.key);
      if (!keysToRemove.length) {
        prevSnapshot = newCart;
        return;
      }

      // Hold the loader continuously for the whole cascade — the N sequential
      // changeLine() fetches each bounce the fetch counter to zero between them,
      // which otherwise makes the overlay flicker on/off. Held only now that we
      // KNOW there is teardown work to do (no-op reconciles never show a loader).
      if (window.__cartLoader && typeof window.__cartLoader.hold === 'function') {
        window.__cartLoader.hold();
        didHoldLoader = true;
      }

      let lastResponse = null;
      for (const key of keysToRemove) {
        lastResponse = await changeLine(key);
      }

      prevSnapshot = await fetchCart();
      refreshCartUISilently(prevSnapshot, lastResponse?.sections, 'bundle-cart-cascade');
    } catch (err) {
      console.error('[bundle-cart] reconcile failed', err);
    } finally {
      // Always release if we grabbed it, even if a cascade fetch threw.
      if (didHoldLoader && window.__cartLoader && typeof window.__cartLoader.release === 'function') {
        window.__cartLoader.release();
      }
      reconcileLock = false;
      if (pendingReconcile) {
        pendingReconcile = false;
        setTimeout(() => reconcile(), 0);
      }
    }
  }

  // ---------- Cart mutation observer (read-only; cascade trigger) ----------

  /** True only for a genuine user removal: POST /cart/change.js with quantity 0. */
  function isRemovalRequest(url, options) {
    if (!/\/cart\/change(\.js)?(\?|$)/.test(url)) return false;
    if ((options?.method || 'GET').toUpperCase() !== 'POST') return false;
    try {
      let body = options?.body;
      if (typeof body === 'string') body = JSON.parse(body);
      else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        const obj = {};
        for (const [k, v] of body.entries()) obj[k] = v;
        body = obj;
      }
      return Number(body?.quantity) === 0;
    } catch {
      return false;
    }
  }

  /** Our own writes carry X-Bundle-Cart-Internal so we never react to them. */
  function isInternalRequest(options) {
    const h = options?.headers;
    if (!h) return false;
    if (typeof h.get === 'function') return h.get('X-Bundle-Cart-Internal') === '1';
    return h['X-Bundle-Cart-Internal'] === '1';
  }

  function bindCartMutations() {
    const original = window.fetch.bind(window);
    window.fetch = async function patchedFetch(input, options) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isCart = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
      const internal = isInternalRequest(options);
      const removal = isRemovalRequest(url, options);

      const response = await original(input, options);

      // Ignore non-cart fetches, failures, and our own internal writes.
      if (!isCart || !response.ok || internal) return response;

      if (removal) {
        // Genuine user removal → cascade. Set pendingReconcile SYNCHRONOUSLY so a
        // concurrent totals-refresh (/cart/update.js) can't clobber the baseline
        // before we diff it. Coalesced behind the lock.
        pendingReconcile = true;
        if (!reconcileLock) setTimeout(() => reconcile(), 80);
      } else {
        // Any other external cart mutation → refresh the baseline snapshot only,
        // but never while a cascade is scheduled or running (would corrupt the diff).
        setTimeout(async () => {
          if (reconcileLock || pendingReconcile) return;
          prevSnapshot = await fetchCart();
        }, 80);
      }

      return response;
    };
  }

  /** Remove a line by key; requests section HTML so the last call can refresh UI. */
  async function changeLine(key) {
    try {
      const res = await fetch(CART_CHANGE_URL, {
        method: 'POST',
        headers: INTERNAL_HEADERS,
        body: JSON.stringify({
          id: key,
          quantity: 0,
          sections: getSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      return res.ok ? await res.json().catch(() => null) : null;
    } catch (err) {
      console.warn('[bundle-cart] cascade remove failed for', key, err);
      return null;
    }
  }

  // ---------- Orphan cleanup (page load, silent) ----------

  async function cleanupOrphans() {
    const cart = await fetchCart();
    const mainBundleIds = new Set(cart.items.filter(isMain).map(bundleIdOf).filter(Boolean));

    // Remove ONLY genuine orphans: free-product lines whose bundle has no
    // surviving main. typeOf() understands every generation of the property
    // scheme (new hidden _product-type, legacy visible product-type, and the
    // original _bundle_role), so intact legacy bundles are recognised and left
    // alone — they keep working through the same cascade path.
    //
    // We deliberately do NOT wipe every legacy `product-type` line: that emptied
    // real customer carts on load and left the drawer showing server-rendered
    // ghost rows, which made Horizon's line-based /cart/change hit a line that no
    // longer existed → 422 on the remove button.
    const orphans = cart.items.filter((it) => {
      // Never touch the shipping-protection line — it's a separate feature with no
      // _product-type / _bundle_id. isFree() already excludes it; this is explicit
      // defense-in-depth in case it's ever mis-tagged.
      if (propsOf(it)._shipping_protection === 'true') return false;
      if (!isFree(it)) return false;
      const bid = bundleIdOf(it);
      return !bid || !mainBundleIds.has(bid);
    });

    if (!orphans.length) {
      prevSnapshot = cart;
      return;
    }

    console.info('[bundle-cart] cleanup removing', orphans.length, 'orphan free line(s)');
    let lastResponse = null;
    for (const it of orphans) {
      lastResponse = await changeLine(it.key);
    }
    prevSnapshot = await fetchCart();

    // Re-sync the cart UI so no ghost rows remain after the silent removal.
    // Suppress auto-open so this never pops the drawer open on page load.
    refreshCartUISilently(prevSnapshot, lastResponse?.sections, 'bundle-cart-cleanup');
  }

  // ---------- Wiring ----------

  /**
   * Claim the main product form(s) that live alongside a bundle or offer group,
   * and neutralise Horizon's declarative `on:submit` so only we perform the add.
   */
  function claimForms() {
    document.querySelectorAll('product-form-component form').forEach((form) => {
      const section = form.closest('.shopify-section') || document;
      if (!section.querySelector('offer-group, bundle-group-component')) return;
      form.dataset.bundleCartOwned = 'true';
      // Empty on:submit → Horizon's delegated handler finds no method and bails.
      form.setAttribute('on:submit', '');
    });
  }

  async function init() {
    // Attach the ATC interceptor first so a failure in cleanup can't leave a
    // claimed form without a handler.
    document.addEventListener('submit', onSubmitCapture, { capture: true });
    document.addEventListener('shopify:section:load', claimForms);

    claimForms();

    // Seed the baseline and run the load-time cleanup BEFORE installing the
    // mutation observer, so cleanup's own removals are never observed.
    await cleanupOrphans();
    bindCartMutations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
